import { Text } from '@mantine/core'
import { GenericTable, TableColumn } from './Table/GenericTable'
import { useGenericTable } from './Table/useGenericTable'
import { useDebouncedFilter } from './Table/useDebouncedFilter'
import { OperationReportRouteEntry } from '@/api/main/types.gen'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface RouteStatisticsReportTableProps {
  data: OperationReportRouteEntry[]
  isLoading?: boolean
  error?: Error | null
}

type RouteStatisticsRow = OperationReportRouteEntry & {
  display_name: string
  total_length_km: number
  mean_velocity_km_h: number
  detections_per_km: number
  sign_posts_per_km: number
  skipped_per_km: number
  unexpected_per_km: number
}

const perKm = (count: number, lengthM: number): number => {
  const km = lengthM / 1000
  if (km === 0) return 0
  return count / km
}

const RouteStatisticsReportTable = ({ data, isLoading = false, error = null }: RouteStatisticsReportTableProps) => {
  const { t } = useTranslation('statistics')

  const transformedData = useMemo(
    () =>
      data.map(item => {
        const km = item.total_sum_length_m / 1000
        const durationH = item.total_sum_duration / 3600
        return {
          ...item,
          display_name: item.route_name || item.route_id,
          total_length_km: km,
          mean_velocity_km_h: durationH > 0 ? km / durationH : 0,
          detections_per_km: perKm(item.total_detection_count, item.total_sum_length_m),
          sign_posts_per_km: perKm(item.total_sign_post_count, item.total_sum_length_m),
          skipped_per_km: perKm(item.total_skipped_segment_count, item.total_sum_length_m),
          unexpected_per_km: perKm(item.total_unexpected_event_count, item.total_sum_length_m),
        }
      }),
    [data]
  )

  const defaultColumns: TableColumn<RouteStatisticsRow>[] = [
    {
      key: 'display_name',
      label: t('route'),
      visible: true,
      sortable: true,
      align: 'left',
    },
    {
      key: 'route_id',
      label: t('routeId'),
      visible: false,
      sortable: true,
      align: 'left',
    },
    {
      key: 'drive_count',
      label: t('drives'),
      tooltip: t('drivesOnRouteTooltip'),
      visible: true,
      sortable: true,
      align: 'right',
    },
    {
      key: 'mean_velocity_km_h',
      label: t('meanPatrolVelocity'),
      tooltip: t('meanPatrolVelocityTooltip'),
      visible: true,
      sortable: true,
      align: 'right',
      render: (item: RouteStatisticsRow) => <Text ta="right">{item.mean_velocity_km_h.toFixed(1)}</Text>,
    },
    {
      key: 'detections_per_km',
      label: t('vehicleDetectionsPerKm'),
      tooltip: t('vehicleDetectionsPerKmTooltip'),
      visible: true,
      sortable: true,
      align: 'right',
      render: (item: RouteStatisticsRow) => <Text ta="right">{item.detections_per_km.toFixed(2)}</Text>,
    },
    {
      key: 'sign_posts_per_km',
      label: t('signPostDetectionsPerKm'),
      tooltip: t('signPostDetectionsPerKmTooltip'),
      visible: true,
      sortable: true,
      align: 'right',
      render: (item: RouteStatisticsRow) => <Text ta="right">{item.sign_posts_per_km.toFixed(2)}</Text>,
    },
    {
      key: 'skipped_per_km',
      label: t('skippedSegmentsPerKm'),
      tooltip: t('skippedSegmentsPerKmTooltip'),
      visible: true,
      sortable: true,
      align: 'right',
      render: (item: RouteStatisticsRow) => <Text ta="right">{item.skipped_per_km.toFixed(2)}</Text>,
    },
    {
      key: 'unexpected_per_km',
      label: t('unexpectedEventsPerKm'),
      tooltip: t('unexpectedEventsPerKmTooltip'),
      visible: true,
      sortable: true,
      align: 'right',
      render: (item: RouteStatisticsRow) => <Text ta="right">{item.unexpected_per_km.toFixed(2)}</Text>,
    },
  ]

  const { filterField, search, filteredData, isDebouncing, handleFilterFieldChange, handleSearchChange } =
    useDebouncedFilter({
      data: transformedData,
      defaultFilterField: 'display_name',
      searchDebounceMs: 300,
    })

  const {
    currentPage,
    tableColumns,
    paginatedData,
    handlePageChange,
    setTableColumns,
    sortDirection,
    setSortDirection,
    sortField,
    setSortField,
  } = useGenericTable({
    defaultSortField: 'display_name',
    defaultSortDirection: 'asc',
    tableKey: 'route-statistics-report-table',
    columns: defaultColumns,
    filteredData,
    itemsPerPage: 25,
  })

  return (
    <GenericTable
      data={transformedData}
      columns={tableColumns}
      onColumnsChange={setTableColumns}
      isLoading={isLoading}
      error={error}
      itemsPerPage={25}
      currentPage={currentPage}
      onPageChange={handlePageChange}
      filterField={filterField}
      onFilterFieldChange={handleFilterFieldChange}
      search={search}
      onSearchChange={handleSearchChange}
      getRowKey={item => item.route_id}
      emptyMessage={t('noRouteStatisticsAvailable')}
      paginatedData={paginatedData}
      filteredData={filteredData}
      isDebouncing={isDebouncing}
      configTitle={t('configureRouteStatisticsView')}
      csvFileName="route-statistics.csv"
      horizontalSpacing="xs"
      verticalSpacing="xs"
      sortDirection={sortDirection}
      onSortDirectionChange={setSortDirection}
      sortField={sortField}
      onSortFieldChange={setSortField}
      stickyHeader
    />
  )
}

export default RouteStatisticsReportTable
